/**
 * GitLab repository API functions
 */

import { fetchBackend } from '../../../apiClient';
import { handleApiError } from './utils';
import type {
  GitlabRepositoryListRequest,
  GitlabRepositoryListResponse,
  GitlabWebhookCreateRequest,
  GitlabWebhookCreateResponse,
  GitlabWebhookDeleteRequest,
  GitlabWebhookStatusRequest,
  GitlabWebhookStatusResponse,
} from '../../../../types/gitlab';

export const listGitlabRepositories = async (
  params: GitlabRepositoryListRequest,
): Promise<GitlabRepositoryListResponse> => {
  const { projectId, search, page = 1, perPage = 20 } = params;

  const searchParams = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
    ...(search != null && search !== '' && { search }),
  });

  const getResponse = await fetchBackend(
    `/gitlab-repositories/${projectId}?${searchParams.toString()}`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    return handleApiError(getResponse);
  }

  return (await getResponse.json()) as GitlabRepositoryListResponse;
};

export const addRepositoryWebhook = async (
  params: GitlabWebhookCreateRequest,
): Promise<GitlabWebhookCreateResponse> => {
  const { projectId, repositoryId, events } = params;

  const postResponse = await fetchBackend(
    `/gitlab-repositories/${projectId}/${repositoryId}/webhook`,
    {
      method: 'POST',
      body: JSON.stringify({
        events: events ?? ['push_events', 'merge_requests_events'],
      }),
    },
  );

  if (!postResponse.ok) {
    return handleApiError(postResponse);
  }

  return (await postResponse.json()) as GitlabWebhookCreateResponse;
};

export const removeRepositoryWebhook = async (
  params: GitlabWebhookDeleteRequest,
): Promise<void> => {
  const { projectId, repositoryId } = params;

  const deleteResponse = await fetchBackend(
    `/gitlab-repositories/${projectId}/${repositoryId}/webhook`,
    {
      method: 'DELETE',
    },
  );

  if (!deleteResponse.ok) {
    return handleApiError(deleteResponse);
  }
};

export const getRepositoryWebhookStatus = async (
  params: GitlabWebhookStatusRequest,
): Promise<GitlabWebhookStatusResponse> => {
  const { projectId, repositoryId } = params;

  const getResponse = await fetchBackend(
    `/gitlab-repositories/${projectId}/${repositoryId}/webhook`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    return handleApiError(getResponse);
  }

  return (await getResponse.json()) as GitlabWebhookStatusResponse;
};
